// src/Dashboard/pages/teacher/Attendance/components/AttendanceAdherentsGrid.jsx

import React, { useEffect } from "react";
import {
  FaBirthdayCake,
  FaCheck,
  FaMinus,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";
import { MdMedicalServices } from "react-icons/md";

import {
  getAdherentName,
  getAdherentPhoto,
  getAttendanceStatusMeta,
} from "../utils/attendanceUtils";

export default function AttendanceAdherentsGrid({
  adherents = [],
  attendance = {},
  savingIds = new Set(),
  assiduityInfo = {},
  birthdaySet = new Set(),
  onToggle,
  requestAssiduity,
}) {
  useEffect(() => {
    if (!adherents.length) return;

    requestAssiduity?.(adherents.map((adherent) => adherent.id));
  }, [adherents, requestAssiduity]);

  return (
    <>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-white">
            {adherents.length} adhérent{adherents.length > 1 ? "s" : ""} affiché
            {adherents.length > 1 ? "s" : ""}
          </h3>

          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Un clic sur une carte change l’état de présence.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 text-xs font-bold">
          <LegendDot className="bg-emerald-500" label="Présent" />
          <LegendDot className="bg-rose-500" label="Absent" />
          <LegendDot className="bg-slate-400" label="Non saisi" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {adherents.map((adherent) => (
          <AttendanceAdherentCard
            key={adherent.id}
            adherent={adherent}
            status={attendance?.[adherent.id] ?? 0}
            saving={savingIds.has(String(adherent.id))}
            assiduityInfo={assiduityInfo?.[adherent.id]}
            isBirthday={birthdaySet.has(adherent.id)}
            onToggle={onToggle}
          />
        ))}
      </div>
    </>
  );
}

function AttendanceAdherentCard({
  adherent,
  status = 0,
  saving = false,
  assiduityInfo,
  isBirthday = false,
  onToggle,
}) {
  const meta = getAttendanceStatusMeta(status);
  const photo = getAdherentPhoto(adherent);
  const name = getAdherentName(adherent);

  const injured = !!adherent?.is_blessed;

  return (
    <button
      type="button"
      onClick={() => onToggle?.(adherent.id)}
      disabled={saving}
      title="Basculer présence"
      className={`group relative overflow-hidden rounded-2xl bg-white text-left shadow-sm ring-1 ring-slate-200/70 transition hover:-translate-y-0.5 hover:shadow-md disabled:cursor-wait disabled:opacity-80 dark:bg-slate-900/70 dark:ring-slate-700/70 ${
        status === 1
          ? "ring-emerald-300 dark:ring-emerald-700"
          : status === 2
          ? "ring-rose-300 dark:ring-rose-700"
          : ""
      }`}
    >
      <div className="relative aspect-square overflow-hidden bg-slate-100 dark:bg-slate-800">
        <img
          src={photo}
          alt={name}
          className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
          draggable={false}
          loading="lazy"
          onError={(event) => {
            event.currentTarget.src = "/img/user_avatar.jpg";
          }}
        />

        {status !== 0 && (
          <div
            className={`absolute inset-0 flex items-center justify-center ${meta.overlay}`}
          >
            <span className="grid h-16 w-16 place-items-center rounded-full bg-white/90 text-3xl shadow-lg">
              {status === 1 ? (
                <FaCheck className="text-emerald-600" />
              ) : (
                <FaTimes className="text-rose-600" />
              )}
            </span>
          </div>
        )}

        {saving && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/35">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-white text-slate-900 shadow-lg">
              <FaSpinner className="animate-spin" />
            </span>
          </div>
        )}

        <div className="absolute left-2 top-2 flex flex-col gap-1.5">
          <AssiduityBadge info={assiduityInfo} />

          {injured && (
            <span
              title="Blessure / absence"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-rose-600 text-white shadow-lg ring-2 ring-white/70"
            >
              <MdMedicalServices />
            </span>
          )}
        </div>

        <div className="absolute right-2 top-2 flex flex-col gap-1.5">
          {isBirthday && (
            <span
              title="Anniversaire"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-pink-500 text-white shadow-lg ring-2 ring-white/70"
            >
              <FaBirthdayCake />
            </span>
          )}

          {status === 0 && (
            <span
              title="Non saisi"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900/85 text-white shadow-lg ring-2 ring-white/70"
            >
              <FaMinus />
            </span>
          )}
        </div>
      </div>

      <div className="space-y-2 p-3">
        <div>
          <p className="line-clamp-1 text-sm font-bold text-slate-900 dark:text-white">
            {(adherent?.nom || "").toUpperCase()}
          </p>

          <p className="line-clamp-1 text-sm font-medium text-slate-600 dark:text-slate-300">
            {adherent?.prenom || "—"}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          {/* <span
            className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ${meta.badge}`}
          >
            {meta.label}
          </span> */}

          {adherent?.statut_inscription && (
            <span className="line-clamp-1 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {adherent.statut_inscription}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function AssiduityBadge({ info }) {
  if (!info) return null;

  const percent = Math.round(Number(info.assiduity || 0));
  const present = Number(info.totalAttendances || 0);
  const total = Number(info.total || 0);

  const className =
    percent >= 75
      ? "bg-emerald-500"
      : percent >= 50
      ? "bg-amber-500"
      : percent > 0
      ? "bg-rose-500"
      : "bg-blue-500";

  return (
    <span
      title="Assiduité"
      className={`inline-flex rounded-full px-2 py-1 text-[11px] font-bold text-white shadow-lg ring-2 ring-white/70 ${className}`}
    >
      {percent}% · {present}/{total}
    </span>
  );
}

function LegendDot({ className, label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      <span className={`h-2.5 w-2.5 rounded-full ${className}`} />
      {label}
    </span>
  );
}